import React, { useState, useRef, useCallback } from 'react';
import EmailItem from './EmailItem';

export default function EmailInputList({ onChangeEmailList, isProcessing = false, processingMessage = '' }) {
  const emailInputRef = useRef(null);

  const [emailList, setEmailList] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [bulkEmailCount, setBulkEmailCount] = useState(0);
  const [bulkEmailList, setBulkEmailList] = useState([]);

  const processEmails = (input) => {
    return input.split(/[,\s]+/).filter((item) => item.match(/.+@.+\..+/));
  };

  const handleEmailInputChange = (event) => {
    setEmailInput(event.target.value);
  };

  const handleEmailInputKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault();
      if (emailInput) {
        const items = processEmails(emailInput);

        setEmailList((prevEmailList) => {
          const updatedEmailList = [...prevEmailList, ...items];
          onChangeEmailList(updatedEmailList);
          return updatedEmailList;
        });

        setEmailInput('');
      }
    } else if (
      event.key === 'Backspace' &&
      emailInput === '' &&
      emailList.length > 0
    ) {
      event.preventDefault();
      setEmailList((prevEmailList) => {
        const lastEmailIndex = prevEmailList.length - 1;
        const lastEmail = prevEmailList[lastEmailIndex];
        setEmailInput(lastEmail);
        const newList = prevEmailList.slice(0, lastEmailIndex);
        onChangeEmailList(newList);
        return newList;
      });
    }
  };

  const handleEmailInputPaste = (event) => {
    event.preventDefault();
    const pastedData = (event.clipboardData || window.clipboardData).getData(
      'text'
    );

    if (pastedData) {
      const items = processEmails(pastedData);

      setEmailList((prevEmailList) => {
        const updatedEmailList = [...prevEmailList, ...items];
        onChangeEmailList(updatedEmailList);
        return updatedEmailList;
      });

      setEmailInput('');
    }
  };

  const handleEmailInputBlur = () => {
    if (emailInput) {
      const items = processEmails(emailInput);

      if (items.length) {
        setEmailList((prevEmailList) => {
          const updatedEmailList = [...prevEmailList, ...items];
          onChangeEmailList(updatedEmailList);
          return updatedEmailList;
        });
      }

      setEmailInput('');
    }
  };

  const removeBulkEmail = useCallback(() => {
    setBulkEmailCount(0);
    setBulkEmailList([]);
  }, []);

  const removeEmail = useCallback((emailToRemove) => {
    setEmailList((prevEmailList) => {
      const filteredList = prevEmailList.filter(
        (email) => email !== emailToRemove
      );
      onChangeEmailList(filteredList);
      return filteredList;
    });
  }, []);

  const handleBulkEmailImport = useCallback((event) => {
    const file = event.target.files[0];
    const reader = new FileReader();

    reader.onload = ({ target }) => {
      const items = target.result
        .split(/\r\n|\n|,/)
        .map(item => item.trim().replace(/"/g, ''))
        .filter((item) => item.match(/.+@.+\..+/) && item.length > 0);

      // If more than BULK_EMAIL_LENGTH emails, don't add to list to avoid browser freeze
      const bulkEmailLimit = parseInt(window.ENV?.BULK_EMAIL_LENGTH || process.env.REACT_APP_BULK_EMAIL_LENGTH || '1', 10);
      if (items.length > bulkEmailLimit) {
        setBulkEmailCount(items.length);
        setBulkEmailList(items); // Store the full list for processing
        // Clear the email list and pass the count to parent
        setEmailList([]);
        // Pass empty email list and bulk count to parent component
        if (onChangeEmailList) {
          onChangeEmailList([], items.length, items);
        }
      } else {
        // For small batches, add to list normally
        setBulkEmailCount(0);
        setBulkEmailList([]);
        setEmailList((prevEmailList) => {
          const updatedList = [...prevEmailList, ...items];
          onChangeEmailList(updatedList);
          return updatedList;
        });
      }
      
      // Reset file input
      event.target.value = '';
    };

    reader.readAsText(file);
  }, [onChangeEmailList]);

  return (
    <>
      <div className="flex flex-row gap-2">
        <p className="text-sm uppercase text-charcoal font-bold my-4">Emails</p>
        <input
          type="file"
          id="user-csv-upload"
          hidden
          onChange={handleBulkEmailImport}
          disabled={isProcessing}
        />
        <label
          htmlFor="user-csv-upload"
          className={`font-bold py-2 px-4 my-2 rounded text-sm ${
            isProcessing 
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed' 
              : 'bg-grey-lightest hover:bg-purple-100 text-charcoal hover:text-link-purple cursor-pointer'
          }`}
        >
          {isProcessing ? 'Processing...' : 'From CSV'}
        </label>
      </div>
      
      {isProcessing && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-2"></div>
            <p className="text-sm text-blue-800">{processingMessage}</p>
          </div>
        </div>
      )}
      
      {bulkEmailCount > 0 && !isProcessing && (
        <div style={{ gap: '8px' }} className={`email-list w-full p-4 flex flex-wrap border-2 border-gray rounded-lg relative`}>
          <div className="email-item shadow flex items-center rounded">
            {bulkEmailCount} emails loaded from CSV
            <button
              type="button"
              className="ml-2 flex items-center justify-center"
              onClick={() => removeBulkEmail()}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  stroke="#6357B5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M18 6 6 18M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
      {bulkEmailCount === 0 && (
        <div
          style={{ gap: '8px' }}
          className={`email-list w-full p-4 flex flex-wrap border-2 border-gray rounded-lg relative ${
            isProcessing ? 'opacity-50 pointer-events-none' : ''
          }`}
          onClick={() => !isProcessing && emailInputRef.current?.focus()}
        >
          {emailInputRef.current !== document.activeElement &&
            emailList.length === 0 && !isProcessing && (
              <p className="text-gray absolute pin-t pin-l m-4 ml-5">
                Enter an email and press Enter or Tab
              </p>
            )}
          {isProcessing && (
            <p className="text-gray absolute pin-t pin-l m-4 ml-5">
              {processingMessage || 'Processing emails...'}
            </p>
          )}
          {emailList.map((email) => (
            <EmailItem 
              key={email} 
              email={email} 
              onRemove={isProcessing ? () => {} : removeEmail} 
            />
          ))}
          {!isProcessing && (
            <input
              ref={emailInputRef}
              type="email"
              className="email-input outline-none bg-transparent border-none"
              value={emailInput}
              onChange={(e) => {
                handleEmailInputChange(e);
                e.target.size = e.target.value.length || 1;
              }}
              onBlur={handleEmailInputBlur}
              onKeyDown={handleEmailInputKeyDown}
              onPaste={handleEmailInputPaste}
              size={emailInput.length || 1}
            />
          )}
        </div>
      )}
    </>
  );
}
